import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { Icon } from '../../components/ui/Icon.js';
import { toastError } from '../../components/ui/Toast.js';
import { relativeTime, formatDuration } from '../../lib/format.js';
import { DISPOSITION_GROUPS, DISPOSITION_GROUP_LABELS, type DispositionCode }
  from '@arcnet/contracts';

/**
 * What the call was (API dispositions.ts).
 *
 * Asked the moment a call ends, because the answer is only cheap then: a week later nobody
 * remembers, and a report of "other" is a report of nothing. The codes are the account's, and
 * some of them ask for a note -- a complaint without a word of explanation is a number on a
 * chart that cannot be acted on.
 *
 * In the workspace it takes the dial pad's place for the wrap-up time (owner, 3 October 2026), so
 * marking the call is the one thing to do then; when the time runs out the agent goes ready and
 * the call can still be marked from Recent calls.
 */

interface LastCall {
  id: string;
  call_uuid: string;
  direction: string;
  from_number: string | null;
  to_number: string | null;
  started_at: string;
  /** When it ended, to tell the call just finished from the one before it. */
  ended_at?: string;
  answered: boolean;
  duration_sec: number | null;
  disposition: string | null;
  note: string | null;
}

/**
 * Marks the call just finished once its record reaches the platform, from outside any component:
 * the agent has pressed Done and gone ready, so the wrap-up card is gone by the time the record
 * lands (owner, 24 October 2026: "i cant wrap up dispo faster than the timeout"). Asks every 1.5 s
 * for a minute, as the card did; never marks the call before this one.
 */
function markWhenRecorded(base: string, endedAt: number, v: { code: string; note: string | null }, onSaved: () => void): void {
  const started = Date.now();
  const tick = async (): Promise<void> => {
    try {
      const d = await api.get<LastCall | null>(`${base}/last-call`);
      const here = !!d && (endedAt <= 1 || Date.parse(d.ended_at ?? d.started_at) >= endedAt - 20_000);
      if (here) {
        await api.put(`${base}/last-call/disposition`, {
          code: v.code, ...(v.note ? { note: v.note } : {}), call_uuid: d!.call_uuid,
        });
        onSaved();
        return;
      }
    } catch (e) {
      if (Date.now() - started > 60_000) { toastError(e); return; }
    }
    if (Date.now() - started > 60_000) {
      toastError(new Error('The call\'s record did not arrive, so its outcome was not saved. Mark it from Recent calls.'));
      return;
    }
    setTimeout(() => { void tick(); }, 1_500);
  };
  void tick();
}

export function WrapUp({ tenantId, extensionId, endedAt, onMarked, embedded, number: endedNumber, required = false }: {
  tenantId: string;
  extensionId: string;
  /** When the workspace last saw a call end, so the panel appears for the call just finished. */
  endedAt: number;
  /** Marked: the agent may leave wrap-up now, where the account requires it (0110). */
  onMarked?: () => void;
  /** Drawn inside another card (the workspace's wrap-up stage), not as a card of its own. */
  embedded?: boolean;
  /** The number of the call just finished, as the phone knew it: shown until its record arrives. */
  number?: string | null | undefined;
  /**
   * The account requires a mark before Ready (0110). The platform then refuses Ready until the
   * mark is saved, so Done waits for the save instead of going ready first and being sent back.
   */
  required?: boolean;
}) {
  const qc = useQueryClient();
  const base = `/v1/tenants/${tenantId}/extensions/${extensionId}`;
  const [note, setNote] = useState('');
  const [chosen, setChosen] = useState<string | null>(null);
  const [needNote, setNeedNote] = useState(false);
  /** Done pressed before the call's record arrived, where the mark is required: saving, then ready. */
  const [waiting, setWaiting] = useState(false);

  const codes = useQuery({
    queryKey: ['disposition-codes', tenantId],
    queryFn: () => api.get<{ data: DispositionCode[] }>(`/v1/tenants/${tenantId}/disposition-codes`),
    staleTime: 10 * 60_000,
  });

  /**
   * The call just finished, once its record has arrived. The switch's record reaches the control
   * plane a second or two after the call ends (agent shipper.ts); until then the seat's last call
   * is the one before, or none. So this asks again every 1.5 s until the answer is a call that
   * ended about when this one did (owner, 6 October 2026: "dispo doesnt fire after the call
   * ends"). The 20 s allows for this computer's clock and the platform's differing; after 30 s
   * of asking, the last call is taken as it is. endedAt 1 means the workspace came back in
   * wrap-up without seeing the call end: whatever the last call is.
   */
  // From when this call's end was seen, so the next call starts its own wait.
  const askedSince = useMemo(() => Date.now(), [endedAt]);
  // This call, by when it ended -- never "whatever the last call is", which before this call's record
  // arrives is the call before it (wrap-up review, 24 October 2026).
  const isThisCall = (d: LastCall | null | undefined): boolean => !!d && (endedAt <= 1
    || Date.parse(d.ended_at ?? d.started_at) >= endedAt - 20_000);
  const call = useQuery({
    queryKey: ['last-call', tenantId, extensionId, endedAt],
    queryFn: () => api.get<LastCall | null>(`${base}/last-call`),
    enabled: endedAt > 0,
    refetchInterval: (q) => (isThisCall(q.state.data) || Date.now() - askedSince > 60_000 ? false : 1_500),
    retry: 3,
    retryDelay: 2_000,
  });

  /**
   * What this call was already marked as, if anything -- from THIS call only. Until its record
   * arrives, "the last call" is the one before, and its outcome showed as chosen and then vanished
   * when this call's record came in (owner, 24 October 2026). And a choice the agent has just made
   * is never replaced by "not marked yet".
   */
  useEffect(() => {
    const d = call.data;
    if (!d || !isThisCall(d) || !d.disposition) return;
    setChosen(d.disposition);
    setNote(d.note ?? '');
  }, [call.data?.call_uuid, call.data?.disposition, call.data?.note]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: (v: { code: string; note: string | null }) =>
      api.put(`${base}/last-call/disposition`, {
        code: v.code, ...(v.note ? { note: v.note } : {}),
        ...(call.data?.call_uuid ? { call_uuid: call.data.call_uuid } : {}),
      }),
    onSuccess: (_r, v) => {
      setChosen(v.code);
      setNeedNote(false);
      void qc.invalidateQueries({ queryKey: ['last-call', tenantId, extensionId] });
      onMarked?.();
    },
  });

  const arrived = isThisCall(call.data) && !!call.data;

  if (endedAt === 0) return null;
  // Waited a minute and no record: nothing to mark it against.
  if (!arrived && Date.now() - askedSince > 60_000) return null;

  const active = (codes.data?.data ?? []).filter((c) => c.is_active);
  if (codes.data && active.length === 0) {
    // Marking required with nothing to mark it as would hold the agent for good: said, not hidden.
    return required
      ? <p className="arc-error" style={{ margin: '4px 0' }}>This account requires calls to be marked, but has no outcomes set up. Ask an admin to add them under Agent codes.</p>
      : null;
  }
  if (active.length === 0) return null;

  const c = arrived ? call.data! : null;
  const number = c ? (c.direction === 'outbound' ? c.to_number : c.from_number) : endedNumber ?? null;
  /**
   * Done: saved now if the call's record is here, or in the background when it lands -- either way
   * the agent goes ready at once rather than waiting out the wrap-up time.
   */
  const done = (): void => {
    if (!chosen) return;
    const code = active.find((x) => x.code === chosen);
    if (code?.requires_note && !note.trim()) { setNeedNote(true); return; }
    const v = { code: chosen, note: note.trim() || null };
    if (arrived) { save.mutate(v); return; }
    const saved = () => { void qc.invalidateQueries({ queryKey: ['last-call', tenantId, extensionId] }); };
    if (required) {
      // The platform will not let them go ready until this is saved: wait for it, then go.
      setWaiting(true);
      markWhenRecorded(base, endedAt, v, () => { saved(); onMarked?.(); });
      return;
    }
    markWhenRecorded(base, endedAt, v, saved);
    onMarked?.();
  };

  /** Choosing only selects: the note can still be added, and Done marks the call. */
  const choose = (code: DispositionCode): void => {
    setChosen(code.code);
    setNeedNote(!!code.requires_note && !note.trim());
  };

  return (
    <section className={embedded ? 'arc-wrap' : 'arc-card arc-card-body arc-wrap'}>
      <div className="arc-row" style={{ justifyContent: 'space-between', gap: 10 }}>
        <div className="arc-stack" style={{ gap: 1, minWidth: 0 }}>
          <strong style={{ fontSize: 13 }}>
            {save.isSuccess ? 'Call marked' : 'What was this call?'}
          </strong>
          <span className="arc-subtle arc-truncate" style={{ fontSize: 11.5 }}>
            {number ?? 'withheld'}
            {c ? <> · {relativeTime(c.started_at)}{c.answered ? ` · ${formatDuration(c.duration_sec ?? 0)}` : ' · no answer'}</>
              : ' · just now'}
          </span>
        </div>
        {save.isSuccess && <Icon name="check" size={15} />}
      </div>

      {/* Grouped, because an agent is choosing between kinds of outcome before they choose a
          word for one: positive, neutral, negative, in that order every time, so the hand goes
          to the right part of the panel without reading it (owner, 24 September 2026). A group
          with nothing in it is not drawn -- an account that never marks anything negative
          should not have an empty heading on every call. */}
      {DISPOSITION_GROUPS.map((group) => {
        const codes = active.filter((c) => c.kind === group);
        if (codes.length === 0) return null;
        return (
          <div key={group} className="arc-wrap-group" data-group={group}>
            <span className="arc-wrap-group-label">{DISPOSITION_GROUP_LABELS[group]}</span>
            <div className="arc-wrap-codes">
              {codes.map((code) => (
                <button key={code.code} type="button"
                  className="arc-btn arc-btn-sm" data-kind={code.kind}
                  data-on={chosen === code.code ? '' : undefined}
                  disabled={save.isPending || waiting}
                  onClick={() => choose(code)}>
                  {code.label}
                </button>
              ))}
            </div>
          </div>
        );
      })}

      {/* The note is always there to add to, and demanded only where the code asks for one. */}
      <div className="arc-row" style={{ gap: 8 }}>
        <input className="arc-input" placeholder={needNote ? 'This one needs a note' : 'Note (optional)'}
          aria-label="Note" aria-invalid={needNote || undefined} value={note}
          onChange={(e) => { setNote(e.target.value); if (e.target.value.trim()) setNeedNote(false); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') done();
          }} />
        <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" disabled={!chosen || save.isPending || waiting}
          onClick={done} title={chosen ? 'Mark the call and go ready' : 'Choose what the call was first'}>
          {save.isPending || waiting ? 'Saving…' : 'Done'}
        </button>
      </div>
      {needNote && (
        <div className="arc-error" style={{ marginTop: 0 }}>
          This one needs a note.
        </div>
      )}
    </section>
  );
}
